import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useSelector } from 'store';
import { useGetUserQuery } from 'store/slices/user';
import { useGetOrgQuery } from 'store/slices/org';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { API_URL } from 'constants/envConstants';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

type OctaneContextType = {
  octaneParentToken: string | undefined;
  octaneActiveToken: string | undefined;
};

export const OctaneContext = createContext({} as OctaneContextType);

export const useOctaneContext = () => useContext(OctaneContext);

const OctaneProvider = ({ children }: { children: ReactNode }) => {
  const tempMetronomeEntitlements = useFeature(
    FeatureFlags.tempMetronomeEntitlements
  ).on;
  const { activeOrgId } = useSelector((state) => state.org);
  const { authToken } = useSelector(({ session }) => session);
  const [octaneParentToken, setOctaneParentToken] = useState<string>('');
  const [octaneActiveToken, setOctaneActiveToken] = useState<string>('');
  const { data: org, isFetching: isFetchingOrg } = useGetOrgQuery(
    activeOrgId ?? skipToken
  );
  const { data: user, isLoading: isLoadingUser } = useGetUserQuery();

  const userParentOrgId = useMemo(() => user?.org, [isLoadingUser]);
  const userActiveOrgId = useMemo(
    () =>
      org?.createdFrom
        ? typeof org?.createdFrom === 'string'
          ? org?.createdFrom
          : org?.createdFrom?.id
        : org?.id,
    [isFetchingOrg]
  );

  const fetchOctaneTokens = async () => {
    if (!userParentOrgId || !userActiveOrgId || tempMetronomeEntitlements)
      return null;
    let response = await fetch(`${API_URL}/billing/auth/`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-ORG-ID': userParentOrgId,
      },
    });
    let data = await response.json();
    if (data && data.token) {
      setOctaneParentToken(data.token);
    }
    response = await fetch(`${API_URL}/billing/auth/`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-ORG-ID': userActiveOrgId,
      },
    });
    data = await response.json();
    if (data && data.token) {
      setOctaneActiveToken(data.token);
    }
    return null;
  };

  useEffect(() => {
    if (!tempMetronomeEntitlements) {
      fetchOctaneTokens();
    }
  }, [userParentOrgId, userActiveOrgId, tempMetronomeEntitlements]);

  return (
    <OctaneContext.Provider value={{ octaneParentToken, octaneActiveToken }}>
      {children}
    </OctaneContext.Provider>
  );
};

export default OctaneProvider;
